import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, Platform, ActivityIndicator } from 'react-native';
import { AntDesign, MaterialIcons, FontAwesome5, Feather } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import styles from '@stylesheet';
import { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import { LineChart, BarChart, DonutChart, AreaChart } from '@utils/chartUtils/createChart';
import { getDataInsights, getFolderComparison, getPredictiveAnalytics, getDataFolders } from '@api/api';

interface TimeSeriesData {
  _id: string;
  datasets: number;
  totalItems: number;
  totalViews: number;
  totalInteractions: number;
  avgProcessingTime: number;
  totalDataSize: number;
  publicDatasets: number;
  privateDatasets: number;
}

interface AnalyticsData {
  timeSeriesData: TimeSeriesData[];
  departmentAnalytics: any[];
  tagAnalytics: any[];
  recentActivity: any[];
  growthMetrics: {
    currentPeriod: number;
    previousPeriod: number;
    growthRate: number;
  };
  performanceMetrics: any;
  summary: {
    totalDatasets: number;
    totalItems: number;
    totalViews: number;
    totalInteractions: number;
    avgDataSize: number;
  };
}

const AdvancedAnalyticsDashboard: React.FC = () => {
  const theme = themeSelector();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [predictiveData, setPredictiveData] = useState<any>(null);
  const [folderComparison, setFolderComparison] = useState<any[]>([]);
  const [availableFolders, setAvailableFolders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedView, setSelectedView] = useState<'overview' | 'comparison' | 'predictions'>('overview');
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);

  // Modern styling
  const cardStyle = getModernCardStyle(theme);
  const textColor = getModernTextColor(theme, 'primary');
  const mutedTextColor = getModernTextColor(theme, 'secondary');
  const successColor = '#22c55e';
  const dangerColor = '#ef4444';
  const warningColor = '#f59e0b';
  const primaryColor = '#3b82f6';

  useEffect(() => {
    loadAnalyticsData();
    loadAvailableFolders();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const [insights, predictions] = await Promise.all([
        getDataInsights({ timeRange, groupBy: 'day' }),
        getPredictiveAnalytics('growth', timeRange === '7d' ? '30d' : timeRange)
      ]);

      if (insights.success) {
        setAnalyticsData(insights.data);
      }

      if (predictions.success) {
        setPredictiveData(predictions.data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableFolders = async () => {
    try {
      const folders = await getDataFolders({ userId: '' });
      setAvailableFolders(folders.slice(0, 10)); // Limit for demo
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const loadFolderComparison = async (folderIds: string[]) => {
    try {
      const comparison = await getFolderComparison(folderIds);
      if (comparison.success) {
        setFolderComparison(comparison.data);
      }
    } catch (error) {
      console.error('Error loading folder comparison:', error);
    }
  };

  // Time range selector
  const TimeRangeSelector = () => (
    <View style={{ flexDirection: 'row', marginBottom: 20 }}>
      {['7d', '30d', '90d', '1y'].map((range) => (
        <Pressable
          key={range}
          onPress={() => setTimeRange(range as any)}
          style={[
            styles.modernButton,
            getModernActionStyle(theme, timeRange === range ? 'blue' : 'grey'),
            { marginRight: 8, paddingHorizontal: 16, paddingVertical: 8 }
          ]}
        >
          <Text style={[{
            color: timeRange === range ? '#ffffff' : mutedTextColor,
            fontSize: 14,
            fontWeight: '600'
          }]}>
            {range.toUpperCase()}
          </Text>
        </Pressable>
      ))}
    </View>
  );

  // View selector
  const ViewSelector = () => (
    <View style={{ flexDirection: 'row', marginBottom: 20 }}>
      {[
        { key: 'overview', label: 'Overview', icon: 'dashboard' },
        { key: 'comparison', label: 'Compare', icon: 'compare' },
        { key: 'predictions', label: 'Trends', icon: 'trending-up' }
      ].map((view) => (
        <Pressable
          key={view.key}
          onPress={() => setSelectedView(view.key as any)}
          style={[
            styles.modernButton,
            getModernActionStyle(theme, selectedView === view.key ? 'blue' : 'grey'),
            { marginRight: 8, paddingHorizontal: 16, paddingVertical: 10, flexDirection: 'row', alignItems: 'center' }
          ]}
        >
          <MaterialIcons 
            name={view.icon as any} 
            size={16} 
            color={selectedView === view.key ? '#ffffff' : mutedTextColor} 
          />
          <Text style={[{
            color: selectedView === view.key ? '#ffffff' : mutedTextColor,
            fontSize: 14,
            fontWeight: '600',
            marginLeft: 6
          }]}>
            {view.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );

  // Summary cards
  const SummaryCards = () => {
    if (!analyticsData) return null;

    const cards = [
      {
        title: 'Total Datasets',
        value: analyticsData.summary.totalDatasets.toLocaleString(),
        icon: <FontAwesome5 name="database" size={20} color="#ffffff" />,
        color: 'blue' as const,
        growth: `${analyticsData.growthMetrics.growthRate > 0 ? '+' : ''}${analyticsData.growthMetrics.growthRate.toFixed(1)}%`
      },
      {
        title: 'Total Items',
        value: analyticsData.summary.totalItems.toLocaleString(),
        icon: <MaterialIcons name="insert-chart" size={20} color="#ffffff" />,
        color: 'green' as const,
        growth: '+12.5%'
      },
      {
        title: 'Total Views',
        value: analyticsData.summary.totalViews.toLocaleString(),
        icon: <FontAwesome5 name="eye" size={20} color="#ffffff" />,
        color: 'amber' as const,
        growth: '+8.3%'
      },
      {
        title: 'Interactions',
        value: analyticsData.summary.totalInteractions.toLocaleString(),
        icon: <MaterialIcons name="touch-app" size={20} color="#ffffff" />,
        color: 'red' as const,
        growth: '+15.7%'
      }
    ];

    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 20 }}>
        {cards.map((card, index) => (
          <View key={card.title} style={{ width: Platform.OS === 'web' ? '24%' : '48%', marginRight: '1%', marginBottom: 16 }}>
            <Animatable.View 
              animation="fadeInUp" 
              duration={600} 
              delay={index * 100}
              style={[styles.modernStatsCard, cardStyle, { padding: 16 }]}
            >
              <View style={styles.modernRow}>
                <View style={[styles.modernActionButton, getModernActionStyle(theme, card.color), { width: 40, height: 40 }]}>
                  {card.icon}
                </View>
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={[styles.modernFieldLabel, { color: mutedTextColor, fontSize: 12 }]}>{card.title}</Text>
                  <Text style={[styles.modernStatsNumber, { color: textColor, fontSize: 18, fontWeight: '700' }]}>
                    {card.value}
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                    <MaterialIcons 
                      name={card.growth.startsWith('+') ? 'trending-up' : 'trending-down'} 
                      size={12} 
                      color={card.growth.startsWith('+') ? successColor : dangerColor} 
                    />
                    <Text style={[{ 
                      fontSize: 11, 
                      color: card.growth.startsWith('+') ? successColor : dangerColor, 
                      marginLeft: 4,
                      fontWeight: '600'
                    }]}>
                      {card.growth}
                    </Text>
                  </View>
                </View>
              </View>
            </Animatable.View>
          </View>
        ))}
      </View>
    );
  };

  // Time series chart
  const TimeSeriesChart = () => {
    if (!analyticsData?.timeSeriesData) return null;

    const chartData = analyticsData.timeSeriesData.map(item => ({
      x: item._id,
      datasets: item.datasets,
      items: item.totalItems,
      views: item.totalViews,
      interactions: item.totalInteractions
    }));

    return (
      <Animatable.View animation="fadeInLeft" duration={600} style={[cardStyle, { padding: 20, marginBottom: 20 }]}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text style={[{ fontSize: 18, fontWeight: '600', color: textColor }]}>Data Activity Over Time</Text>
          <View style={{ flexDirection: 'row' }}>
            <Pressable style={[styles.modernButton, getModernActionStyle(theme, 'grey'), { marginRight: 8 }]}>
              <Text style={[{ fontSize: 12, color: mutedTextColor }]}>Datasets</Text>
            </Pressable>
            <Pressable style={[styles.modernButton, getModernActionStyle(theme, 'blue')]}>
              <Text style={[{ fontSize: 12, color: '#ffffff' }]}>Views</Text>
            </Pressable>
          </View>
        </View>

        <View style={{ height: 250 }}>
          <AreaChart 
            seriesData={[{ name: 'Views', data: chartData.map(item => ({ x: item.x, y: item.views })) }]}
            categories={chartData.map(item => item.x)}
            chartColor={primaryColor}
            height={250}
          />
        </View>
      </Animatable.View>
    );
  };

  // Department analytics
  const DepartmentAnalytics = () => {
    if (!analyticsData?.departmentAnalytics) return null;

    return (
      <Animatable.View animation="fadeInRight" duration={600} style={[cardStyle, { padding: 20, marginBottom: 20 }]}>
        <Text style={[{ fontSize: 18, fontWeight: '600', color: textColor, marginBottom: 16 }]}>
          Department Breakdown
        </Text>

        <View style={{ height: 200, marginBottom: 16 }}>
          <DonutChart 
            series={analyticsData.departmentAnalytics.map(dept => dept.count)} 
            labels={analyticsData.departmentAnalytics.map(dept => dept._id || 'Unknown')}
          />
        </View>

        <View>
          {analyticsData.departmentAnalytics.slice(0, 5).map((dept, index) => (
            <View key={dept._id || index} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: 6, 
                  backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
                  marginRight: 8 
                }} />
                <Text style={[{ fontSize: 14, color: textColor }]}>{dept._id || 'Unknown'}</Text>
              </View>
              <Text style={[{ fontSize: 14, color: mutedTextColor, fontWeight: '600' }]}>{dept.count}</Text>
            </View>
          ))}
        </View>
      </Animatable.View>
    );
  };

  // Predictive analytics
  const PredictiveChart = () => {
    if (!predictiveData) return null;

    const combinedData = [
      ...predictiveData.historical.map((item: any) => ({ x: item._id, y: item.datasets, type: 'historical' })),
      ...predictiveData.predictions.map((item: any) => ({ x: item.date, y: item.predicted, type: 'predicted' }))
    ];

    return (
      <Animatable.View animation="fadeInUp" duration={600} style={[cardStyle, { padding: 20, marginBottom: 20 }]}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text style={[{ fontSize: 18, fontWeight: '600', color: textColor }]}>Growth Predictions</Text>
          <View style={[{ backgroundColor: successColor + '20', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12 }]}>
            <Text style={[{ fontSize: 12, color: successColor, fontWeight: '600' }]}>
              {predictiveData.trendDirection === 'upward' ? '↗️ Growing' : '↘️ Declining'}
            </Text>
          </View>
        </View>

        <View style={{ height: 200 }}>
          <LineChart 
            data={combinedData.map(item => ({ x: item.x, y: item.y }))}
            predictedStartIndex={predictiveData.historical.length}
          />
        </View>

        <Text style={[{ fontSize: 12, color: mutedTextColor, marginTop: 8, textAlign: 'center' }]}>
          Confidence: {(predictiveData.confidence * 100).toFixed(0)}% • 
          Trend: {predictiveData.trendDirection}
        </Text>
      </Animatable.View>
    );
  };

  // Folder comparison view
  const FolderComparisonView = () => (
    <View>
      <Text style={[{ fontSize: 18, fontWeight: '600', color: textColor, marginBottom: 16 }]}>
        Compare Data Folders
      </Text>
      
      {/* Folder selector */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 20 }}>
        {availableFolders.map((folder) => (
          <Pressable
            key={folder._id}
            onPress={() => {
              const isSelected = selectedFolders.includes(folder._id);
              const newSelection = isSelected 
                ? selectedFolders.filter(id => id !== folder._id)
                : [...selectedFolders, folder._id].slice(0, 3); // Max 3 folders
              
              setSelectedFolders(newSelection);
              if (newSelection.length > 0) {
                loadFolderComparison(newSelection);
              }
            }}
            style={[
              styles.modernButton,
              getModernActionStyle(theme, selectedFolders.includes(folder._id) ? 'blue' : 'grey'),
              { marginRight: 8, paddingHorizontal: 16, paddingVertical: 10 }
            ]}
          >
            <Text style={[{
              color: selectedFolders.includes(folder._id) ? '#ffffff' : mutedTextColor,
              fontSize: 14,
              fontWeight: '500'
            }]}>
              {folder.dataTitle}
            </Text>
          </Pressable>
        ))}
      </ScrollView>

      {/* Comparison chart */}
      {folderComparison.length > 0 && (
        <Animatable.View animation="fadeIn" duration={600} style={[cardStyle, { padding: 20 }]}>
          <Text style={[{ fontSize: 16, fontWeight: '600', color: textColor, marginBottom: 16 }]}>
            Performance Comparison
          </Text>

          <View style={{ height: 250 }}>
            <BarChart
              seriesData={folderComparison.map((folder, index) => ({
                x: folder.dataTitle.substring(0, 10) + '...',
                y: folder.itemCount
              }))}
              categories={['Items', 'Views', 'Interactions']}
            />
          </View>

          <View style={{ marginTop: 16 }}>
            {folderComparison.map((folder, index) => (
              <View key={folder._id} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text style={[{ fontSize: 14, color: textColor, flex: 1 }]}>{folder.dataTitle}</Text>
                <Text style={[{ fontSize: 14, color: mutedTextColor }]}>{folder.itemCount} items</Text>
                <Text style={[{ fontSize: 14, color: mutedTextColor, marginLeft: 16 }]}>{folder.totalViews} views</Text>
              </View>
            ))}
          </View>
        </Animatable.View>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={primaryColor} />
        <Text style={[{ color: textColor, marginTop: 16 }]}>Loading analytics...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, padding: 16 }} showsVerticalScrollIndicator={false}>
      <Animatable.View animation="fadeInDown" duration={600}>
        <Text style={[{ fontSize: 24, fontWeight: '700', color: textColor, marginBottom: 8 }]}>
          Data Analytics Dashboard
        </Text>
        <Text style={[{ fontSize: 16, color: mutedTextColor, marginBottom: 20 }]}>
          Comprehensive insights into your data performance
        </Text>

        <TimeRangeSelector />
        <ViewSelector />
      </Animatable.View>

      {selectedView === 'overview' && (
        <>
          <SummaryCards />
          <TimeSeriesChart />
          <View style={{ flexDirection: Platform.OS === 'web' ? 'row' : 'column' }}>
            <View style={{ flex: Platform.OS === 'web' ? 2 : 1, marginRight: Platform.OS === 'web' ? 16 : 0 }}>
              <DepartmentAnalytics />
            </View>
            <View style={{ flex: Platform.OS === 'web' ? 1 : 1 }}>
              <PredictiveChart />
            </View>
          </View>
        </>
      )}

      {selectedView === 'comparison' && <FolderComparisonView />}

      {selectedView === 'predictions' && (
        <View>
          <PredictiveChart />
          <DepartmentAnalytics />
        </View>
      )}
    </ScrollView>
  );
};

export default AdvancedAnalyticsDashboard;
